/**
 * Time-zone catalog helpers.
 *
 * The product is multi-market: a user in London watching US equities wants the
 * clock and every timestamp rendered in a chosen zone, not the browser's. These
 * helpers provide the zone list (the full IANA set when the runtime supports it)
 * and a display label.
 */

/** Curated fallback used when `Intl.supportedValuesOf` is unavailable. */
const FALLBACK_TIME_ZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Zurich",
  "Europe/Moscow",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

/** The browser's local IANA zone, or `UTC` when it cannot be resolved. */
export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

let cachedZones: string[] | null = null;

/**
 * Every selectable IANA time zone, sorted. Uses `Intl.supportedValuesOf` when
 * present (all modern browsers) and degrades to a curated list otherwise. The
 * result is cached for the process lifetime.
 */
export function listTimeZones(): string[] {
  if (cachedZones) return cachedZones;
  let zones: string[] = [];
  try {
    const supported = (
      Intl as unknown as {
        supportedValuesOf?: (key: string) => string[];
      }
    ).supportedValuesOf;
    if (typeof supported === "function") zones = supported("timeZone");
  } catch {
    zones = [];
  }
  if (!Array.isArray(zones) || zones.length === 0) {
    zones = [...FALLBACK_TIME_ZONES];
  }
  const set = new Set(zones.filter((z) => typeof z === "string" && z.length > 0));
  set.add("UTC");
  cachedZones = [...set].sort((a, b) => a.localeCompare(b));
  return cachedZones;
}

/** Whether a value is a usable IANA zone id (structurally). */
export function isTimeZone(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/** A human-friendly label for a zone id (`America/New_York` → `America · New York`). */
export function humanizeTimeZone(zone: string): string {
  return zone.replace(/_/g, " ").replace(/\//g, " · ");
}
